import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import React__default, { useState, useId, useCallback } from 'react';
import { Button } from '../atoms/Button.js';
import { toSpacingAttributes } from '../layouts/Spacing/spacing.js';
import './Accordion.scss';

/**
 * A collapsible content container that allows users to toggle the visibility of its content.
 *
 * ```tsx
 * <Accordion>
 *   <AccordionHeader>
 * 	   <Text>Accordion Title</Text>
 *   </AccordionHeader>
 *   <AccordionBody>
 * 	   <Text>Accordion Body</Text>
 *   </AccordionBody>
 * </Accordion>
 * ```
 */
const Accordion = (props) => {
    const { className, children, defaultExpanded = false, isExpanded: controlledIsExpanded, isDisabled = false, onExpandedChange, } = props;
    const [internalIsExpanded, setInternalIsExpanded] = useState(defaultExpanded);
    const headerId = useId();
    const panelId = useId();
    // Use controlled state if provided, otherwise use internal state
    const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
    const toggle = useCallback(() => {
        if (isDisabled)
            return;
        const newExpanded = !isExpanded;
        if (controlledIsExpanded === undefined) {
            // Uncontrolled mode - update internal state
            setInternalIsExpanded(newExpanded);
        }
        // Always call the callback if provided
        onExpandedChange?.(newExpanded);
    }, [isDisabled, isExpanded, controlledIsExpanded, onExpandedChange]);
    // Clone children and pass accordion state as props
    const childrenWithProps = React__default.Children.map(children, (child) => {
        if (React__default.isValidElement(child)) {
            if (child.type === AccordionHeader) {
                return React__default.cloneElement(child, {
                    isExpanded,
                    isDisabled,
                    toggle,
                    headerId,
                    panelId,
                });
            }
            if (child.type === AccordionBody) {
                return React__default.cloneElement(child, {
                    isExpanded,
                    headerId,
                    panelId,
                });
            }
        }
        return child;
    });
    return (React__default.createElement("div", { className: className, "data-rev-component": 'accordion' }, childrenWithProps));
};
/**
 * The header component of the Accordion, which serves as the clickable element to toggle the visibility of the Accordion's content.
 *
 * ```tsx
 * <Accordion>
 *   <AccordionHeader>
 * 	     Accordion Title
 *   </AccordionHeader>
 *   <AccordionBody>
 *       Accordion Body
 *   </AccordionBody>
 * </Accordion>
 * ```
 */
const AccordionHeader = (props) => {
    const { className, children, isExpanded, isDisabled, ariaCurrent, toggle, headerId, panelId, variant, icon, } = props;
    if (!toggle || !headerId || !panelId) {
        throw new Error('AccordionHeader must be used within an Accordion component');
    }
    return (React__default.createElement(Button, { id: headerId, ariaExpanded: isExpanded, ariaControls: panelId, isDisabled: isDisabled, ariaCurrent: ariaCurrent, onPress: toggle, size: 'large', className: classNames(className, 'accordion-header--rev'), variant: variant ?? null, icon: icon, iconSide: 'left' },
        children,
        React__default.createElement(ChevronDown, { className: 'accordion-header-icon--rev' })));
};
const ChevronDown = (props) => (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '10', height: '6', viewBox: '0 0 10 6', fill: 'none', ...props },
    React__default.createElement("path", { d: 'M8.21962 0.21967C8.51252 -0.0732233 8.98728 -0.0732232 9.28017 0.21967C9.5727 0.512592 9.57294 0.987444 9.28017 1.28022L5.28017 5.28022C4.98739 5.5729 4.51252 5.57272 4.21962 5.28022L0.219624 1.28022C-0.0732386 0.987354 -0.0731774 0.512571 0.219624 0.21967C0.512517 -0.0732233 0.987278 -0.0732233 1.28017 0.21967L4.7499 3.6894L8.21962 0.21967Z', fill: 'currentColor' })));
/**
 * The body component of the Accordion, which contains the content that is shown or hidden when the Accordion is toggled.
 *
 * ```tsx
 * <Accordion>
 *   <AccordionHeader>
 * 	   <Text>Accordion Title</Text>
 *   </AccordionHeader>
 *   <AccordionBody>
 * 	   <Text>Accordion Body</Text>
 *   </AccordionBody>
 * </Accordion>
 * ```
 */
const AccordionBody = (props) => {
    const { className, children, isExpanded, headerId, panelId, itemsSpacing } = props;
    if (headerId === undefined || panelId === undefined) {
        throw new Error('AccordionBody must be used within an Accordion component');
    }
    const animationDuration = 200; // ms
    return (React__default.createElement(AnimatePresence, { initial: false }, isExpanded && (React__default.createElement(motion.div, { id: panelId, role: 'region', "aria-labelledby": headerId, className: classNames(className, 'accordion-body--rev'), ...toSpacingAttributes('g', itemsSpacing), initial: { height: 0, opacity: 0 }, animate: { height: 'auto', opacity: 1 }, exit: { height: 0, opacity: 0 }, transition: {
            height: {
                duration: animationDuration / 1000,
                ease: [0.4, 0, 0.2, 1],
            },
            opacity: {
                duration: animationDuration / 1000,
                ease: [0.25, 0.1, 0.25, 1],
            },
        } }, children))));
};

export { Accordion, AccordionBody, AccordionHeader };
//# sourceMappingURL=Accordion.js.map
